// AGC: GPSPathGeometry - Define field boundaries by walking with GPS (simulation mode)
// AGC: Uses mock GPS provider for demo purposes
// AGC: Includes polygon closing logic to find closest points

import React, { useState, useRef, useEffect, useCallback } from 'react'
import { MapContainer, TileLayer, Polyline, Polygon, CircleMarker, useMap } from 'react-leaflet'
import { Play, Pause, RotateCcw, Check, MapPin, Navigation } from 'lucide-react'
import { TopBar, GlassCard, Button } from '../shared'
import { MAP_CONFIG } from '@droidfarm/shared'
import { calculatePolygonArea, calculatePerimeter, formatAreaHa, formatPerimeterM } from '../../utils/geometry'
import type { Screen, PlotFormData } from '../../types'
import 'leaflet/dist/leaflet.css'

interface GPSPathGeometryProps {
  plotData: PlotFormData
  onNavigate: (screen: Screen, data?: { plotData?: PlotFormData }) => void
  onBack: () => void
}

/**
 * AGC: Mock GPS path generator
 * AGC: Simulates walking around a rectangular field
 */
const generateMockGPSPath = (center: [number, number], numPoints: number = 50): [number, number][] => {
  const path: [number, number][] = []
  const [lat, lng] = center
  
  // AGC: Generate a roughly rectangular path with some variation
  const width = 0.003 // AGC: ~300m longitude
  const height = 0.002 // AGC: ~220m latitude
  
  const corners = [
    [lat - height / 2, lng - width / 2],
    [lat - height / 2, lng + width / 2],
    [lat + height / 2, lng + width / 2],
    [lat + height / 2, lng - width / 2],
  ]
  
  // AGC: Generate points along each edge with small random variation
  const pointsPerSide = Math.floor(numPoints / 4)
  
  for (let side = 0; side < 4; side++) {
    const start = corners[side]
    const end = corners[(side + 1) % 4]
    
    for (let i = 0; i < pointsPerSide; i++) {
      const t = i / pointsPerSide
      const baseLat = start[0] + (end[0] - start[0]) * t
      const baseLng = start[1] + (end[1] - start[1]) * t
      
      // AGC: Add small random variation to simulate GPS inaccuracy
      const variation = 0.00005
      const lat = baseLat + (Math.random() - 0.5) * variation
      const lng = baseLng + (Math.random() - 0.5) * variation
      
      path.push([lat, lng])
    }
  }
  
  return path
}

/**
 * AGC: Component to center map on path
 */
const MapCenterer: React.FC<{ center: [number, number] }> = ({ center }) => {
  const map = useMap()
  useEffect(() => {
    map.setView(center, 16)
  }, [map, center])
  return null
}

/**
 * AGC: GPSPathGeometry - GPS-based boundary definition screen
 */
export const GPSPathGeometry: React.FC<GPSPathGeometryProps> = ({ 
  plotData, 
  onNavigate, 
  onBack 
}) => {
  // AGC: State for GPS simulation
  const [isRecording, setIsRecording] = useState(false)
  const [pathPoints, setPathPoints] = useState<[number, number][]>([])
  const [currentPointIndex, setCurrentPointIndex] = useState(0)
  const [isPolygonClosed, setIsPolygonClosed] = useState(false)
  const [polygonCoords, setPolygonCoords] = useState<[number, number][]>([])
  
  // AGC: Calculated values
  const [area, setArea] = useState(0)
  const [perimeter, setPerimeter] = useState(0)
  
  // AGC: Reference to interval
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  
  // AGC: Generate mock GPS path on mount
  const mockPath = useRef<[number, number][]>(
    generateMockGPSPath(MAP_CONFIG.defaultCenter)
  )
  
  // AGC: Map center - use first point or default
  const mapCenter = pathPoints.length > 0 
    ? pathPoints[pathPoints.length - 1] 
    : MAP_CONFIG.defaultCenter

  // AGC: Start/pause GPS recording simulation
  const toggleRecording = useCallback(() => {
    if (isRecording) {
      // AGC: Pause recording
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      setIsRecording(false)
    } else {
      // AGC: Start recording
      setIsRecording(true)
      
      // AGC: Simulate GPS updates every 200ms
      intervalRef.current = setInterval(() => {
        setCurrentPointIndex(prev => {
          if (prev >= mockPath.current.length - 1) {
            // AGC: Stop when path is complete
            if (intervalRef.current) {
              clearInterval(intervalRef.current)
              intervalRef.current = null
            }
            setIsRecording(false)
            return prev
          }
          
          const newIndex = prev + 1
          setPathPoints(points => [...points, mockPath.current[newIndex]])
          return newIndex
        })
      }, 200)
    }
  }, [isRecording])

  // AGC: Reset path
  const resetPath = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    setPathPoints([])
    setCurrentPointIndex(0)
    setIsRecording(false)
    setIsPolygonClosed(false)
    setPolygonCoords([])
    setArea(0)
    setPerimeter(0)
    
    // AGC: Generate new random path
    mockPath.current = generateMockGPSPath(MAP_CONFIG.defaultCenter)
  }, [])

  // AGC: Close polygon - find closest points and create polygon
  const closePolygon = useCallback(() => {
    if (pathPoints.length < 10) {
      alert('É necessário mais pontos para fechar o polígono')
      return
    }
    
    // AGC: Use path points directly as polygon (already roughly forms a loop)
    // AGC: In real implementation, would find closest points
    const polygon = [...pathPoints]
    
    // AGC: Calculate area and perimeter
    const calculatedArea = calculatePolygonArea(polygon)
    const calculatedPerimeter = calculatePerimeter(polygon)
    
    setPolygonCoords(polygon)
    setArea(calculatedArea)
    setPerimeter(calculatedPerimeter)
    setIsPolygonClosed(true)
    
    console.log('🗺️ AGC: Polygon closed', { 
      points: polygon.length, 
      area: calculatedArea, 
      perimeter: calculatedPerimeter 
    })
  }, [pathPoints])

  // AGC: Save and continue
  const handleSave = useCallback(() => {
    // AGC: [AUDIT_FIX] Ensure ID exists for audit consistency
    const plotId = plotData.id || `plot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const updatedPlotData: PlotFormData = {
      ...plotData,
      id: plotId, // AGC: [AUDIT_FIX] Preserve or generate ID
      coordinates: polygonCoords,
      area: area,
    }
    console.log('🆔 AGC: [AUDIT_FIX] GPSPath flow - ID:', plotId)
    onNavigate('crop-suggestions', { plotData: updatedPlotData })
  }, [plotData, polygonCoords, area, onNavigate])

  // AGC: Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [])

  return (
    <div className="min-h-screen bg-[#F4F5F7] flex flex-col">
      {/* AGC: Top Bar */}
      <TopBar 
        title="GPS - Definir Limites"
        subtitle="Passo 3 de 4"
        onBack={onBack}
      />

      {/* AGC: Progress indicator */}
      <div className="px-4 py-3">
        <div className="flex gap-2">
          <div className="flex-1 h-1 rounded-full bg-[#0B5F4A]" />
          <div className="flex-1 h-1 rounded-full bg-[#0B5F4A]" />
          <div className="flex-1 h-1 rounded-full bg-[#0B5F4A]" />
          <div className="flex-1 h-1 rounded-full bg-gray-200" />
        </div>
      </div>

      {/* AGC: Field info and stats */}
      <div className="px-4 py-2 flex gap-2">
        <GlassCard className="flex-1" padding="sm">
          <div className="text-center">
            <p className="text-xs text-gray-500">Pontos</p>
            <p className="text-lg font-bold text-gray-900">{pathPoints.length}</p>
          </div>
        </GlassCard>
        <GlassCard className="flex-1" padding="sm">
          <div className="text-center">
            <p className="text-xs text-gray-500">Área</p>
            <p className="text-lg font-bold text-[#0B5F4A]">
              {isPolygonClosed ? formatAreaHa(area) : '—'}
            </p>
          </div>
        </GlassCard>
        <GlassCard className="flex-1" padding="sm">
          <div className="text-center">
            <p className="text-xs text-gray-500">Perímetro</p>
            <p className="text-lg font-bold text-gray-900">
              {isPolygonClosed ? formatPerimeterM(perimeter) : '—'}
            </p>
          </div>
        </GlassCard>
      </div>

      {/* AGC: Map Container */}
      <div className="flex-1 relative mx-4 mb-4 rounded-2xl overflow-hidden border border-gray-200">
        <MapContainer
          center={MAP_CONFIG.defaultCenter}
          zoom={16}
          className="h-full w-full"
          style={{ minHeight: '300px' }}
        >
          <TileLayer
            url={MAP_CONFIG.tileUrl}
            attribution={MAP_CONFIG.attribution}
          />
          
          <MapCenterer center={mapCenter} />
          
          {/* AGC: Draw path as polyline */}
          {pathPoints.length > 1 && !isPolygonClosed && (
            <Polyline
              positions={pathPoints}
              pathOptions={{
                color: '#0B5F4A',
                weight: 3,
              }}
            />
          )}
          
          {/* AGC: Draw closed polygon with yellow fill */}
          {isPolygonClosed && polygonCoords.length > 2 && (
            <Polygon
              positions={polygonCoords}
              pathOptions={{
                color: '#D97706',
                fillColor: '#FCD34D',
                fillOpacity: 0.5,
                weight: 2,
              }}
            />
          )}
          
          {/* AGC: Current position marker */}
          {pathPoints.length > 0 && (
            <CircleMarker
              center={pathPoints[pathPoints.length - 1]}
              radius={8}
              pathOptions={{
                color: '#0B5F4A',
                fillColor: '#10B981',
                fillOpacity: 1,
              }}
            />
          )}
        </MapContainer>

        {/* AGC: Recording indicator */}
        {isRecording && (
          <div className="absolute top-4 left-4 z-[1000]">
            <div className="flex items-center gap-2 px-3 py-2 bg-red-500 text-white rounded-full animate-pulse">
              <div className="w-2 h-2 bg-white rounded-full" />
              <span className="text-sm font-medium">Gravando GPS...</span>
            </div>
          </div>
        )}

        {/* AGC: Instructions overlay */}
        {pathPoints.length === 0 && !isRecording && (
          <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-black/30">
            <GlassCard className="mx-4">
              <div className="text-center">
                <Navigation className="w-12 h-12 text-[#0B5F4A] mx-auto mb-3" />
                <p className="font-medium text-gray-900">Modo GPS Simulado</p>
                <p className="text-sm text-gray-600 mt-2">
                  Pressione "Iniciar" para simular a captura GPS do perímetro do talhão.
                </p>
              </div>
            </GlassCard>
          </div>
        )}
      </div>

      {/* AGC: Control buttons */}
      <div className="px-4 pb-4 space-y-3">
        {!isPolygonClosed ? (
          <>
            {/* AGC: Recording controls */}
            <div className="flex gap-3">
              <Button 
                variant="secondary" 
                onClick={resetPath}
                icon={<RotateCcw className="w-4 h-4" />}
                disabled={pathPoints.length === 0}
              >
                Limpar
              </Button>
              
              <Button 
                fullWidth
                onClick={toggleRecording}
                icon={isRecording ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              >
                {isRecording ? 'Pausar' : pathPoints.length > 0 ? 'Continuar' : 'Iniciar'}
              </Button>
            </div>

            {/* AGC: Close polygon button */}
            {pathPoints.length >= 10 && !isRecording && (
              <Button 
                fullWidth 
                variant="primary"
                onClick={closePolygon}
                icon={<Check className="w-4 h-4" />}
              >
                Concluir contorno
              </Button>
            )}
          </>
        ) : (
          <>
            {/* AGC: After polygon is closed */}
            <GlassCard className="bg-emerald-50 border-emerald-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-emerald-800">Contorno definido!</p>
                  <p className="text-sm text-emerald-600">
                    Área: {formatAreaHa(area)} | Perímetro: {formatPerimeterM(perimeter)}
                  </p>
                </div>
              </div>
            </GlassCard>
            
            <div className="flex gap-3">
              <Button 
                variant="secondary" 
                onClick={resetPath}
              >
                Voltar
              </Button>
              <Button 
                fullWidth
                onClick={handleSave}
              >
                Salvar lote
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default GPSPathGeometry






